/* STEP: Click Post / Direct Integration. Either/or choice of how this courier
   account is integrated. Click Post keeps the existing flow (CP Id, moved here
   from Account Code). Direct Integration (backend: PCIS) collects the courier-
   account fields the courier-accounts API needs — these are entirely different
   per courier (see diCourierSchemas.js, lifted from Courier_Onboarding_Guide.docx),
   so the field list below is resolved dynamically from the parent courier already
   chosen earlier in this wizard plus the Direction/Mode from the Direction, Mode &
   Service Type step — the user isn't asked to pick the courier or its variant again. */
import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import CircularProgress from '@mui/material/CircularProgress'
import {cx} from '../../components'
import {apiError} from '../../supplyMeta'
import {Field, Footer, scrollToFirstError} from './fieldKit'
import {resolveCourierSchemaKey, getCourierSchema, diServiceType} from '../diCourierSchemas'
import {useLazyLookupCourierPartnersQuery} from '../../../../redux/supplyApiSlice'

const CSS = `
.mdsec{margin-bottom:16px}
.mdg2{display:grid;grid-template-columns:1fr 1fr;gap:12px}@media(max-width:820px){.mdg2{grid-template-columns:1fr}}
.mdcard{border:1px solid var(--border);border-radius:10px;padding:12px 13px;cursor:pointer;display:flex;gap:10px;align-items:flex-start;background:#fff;transition:border-color .15s,background .15s}
.mdcard:hover{border-color:var(--primary)}.mdcard.sel{border-color:var(--primary);background:var(--primary-l)}
.mdcard .t{font-weight:700;font-size:12.5px}.mdcard .d{font-size:11px;color:var(--neutral);margin-top:2px;line-height:1.4}
.mdcard .tick{margin-left:auto;width:16px;height:16px;border-radius:50%;border:1.5px solid var(--border);flex:none}.mdcard.sel .tick{background:var(--primary);border-color:var(--primary)}
.mdpp{display:flex;flex-direction:column;gap:8px}
.mdtarget{display:flex;align-items:center;gap:10px;border:1px solid var(--border);border-radius:10px;background:var(--row-alt);padding:10px 14px;margin-bottom:16px}
.mdtarget .ic{width:30px;height:30px;border-radius:8px;background:var(--ink-2);color:#fff;display:flex;align-items:center;justify-content:center;flex:none}
.mdtarget .lbl{font-size:10px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:var(--neutral)}
.mdtarget .val{font-size:14px;font-weight:800;color:var(--ink)}
.xfhead{display:flex;align-items:center;justify-content:space-between;margin:18px 0 8px}
.xfhead .t{font-weight:700;font-size:12.5px}.xfhead .s{font-size:11px;color:var(--neutral);margin-top:2px}
.xfrow{display:grid;grid-template-columns:1fr 1fr 34px;gap:10px;align-items:start;margin-bottom:8px}
.xfrow .in{width:100%}
.xfrow .err{border-color:var(--error,#DC2626)!important}
.xfdel{width:34px;height:34px;border:1px solid var(--border);border-radius:8px;background:#fff;color:var(--neutral);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:border-color .15s,color .15s}
.xfdel:hover{border-color:var(--error,#DC2626);color:var(--error,#DC2626)}
.xfadd{display:inline-flex;align-items:center;gap:6px;border:1px dashed var(--border);border-radius:8px;background:#fff;color:var(--primary);font-weight:700;font-size:12.5px;padding:8px 12px;cursor:pointer;transition:border-color .15s,background .15s}
.xfadd:hover{border-color:var(--primary);background:var(--primary-l)}
`

const YN = ['Yes', 'No']

export default function StepMode({W, dispatch, goBack, goNext}) {
  const DI = W.DI
  const [inv, setInv] = useState({})
  const isNewParent = W.parentMode === 'new'

  const parentName = isNewParent ? W.A.name || '' : W.D.parentId || ''
  const schemaKey = resolveCourierSchemaKey(parentName, W.S.direction)
  const schema = getCourierSchema(schemaKey)

  const clearInv = (k) => setInv((p) => ({...p, [k]: false}))

  const setMode = (mode) => {
    if (mode === DI.mode) return
    dispatch({type: 'FIELD', slice: 'DI', key: 'mode', value: mode})
    setInv({})
  }
  const set = (key, value) => {
    dispatch({type: 'FIELD', slice: 'DI', key, value})
    clearInv(key)
    if (key === 'partnerCode') autoSuggested.current = false // user typed their own value
  }
  const setField = (key, value) => {
    dispatch({type: 'FIELD', slice: 'DI', key: 'fields', value: {...(DI.fields || {}), [key]: value}})
    clearInv(key)
  }

  /* Additional (custom) credential fields — free-form key/value pairs the user adds
     on top of the courier's known schema. Merged into `credentials` at commit time
     (see StepReview.js diCredentials). A row that's added must have both key and
     value filled, or be removed — enforced in onNext(). */
  const extras = DI.extras || []
  const setExtras = (next) => dispatch({type: 'FIELD', slice: 'DI', key: 'extras', value: next})
  const addExtra = () => setExtras([...extras, {key: '', value: ''}])
  const updateExtra = (i, patch) => {
    setExtras(extras.map((x, idx) => (idx === i ? {...x, ...patch} : x)))
    if (patch.key !== undefined) clearInv(`x${i}k`)
    if (patch.value !== undefined) clearInv(`x${i}v`)
  }
  const removeExtra = (i) => {
    setExtras(extras.filter((_, idx) => idx !== i))
    setInv((p) => ({...p, [`x${i}k`]: false, [`x${i}v`]: false}))
  }

  /* suggest the parent's own name (from courierPartner/parent/list, e.g. "Shadowfax")
     as the partner code default, still editable. This is NOT the resolved schema key
     — schemaKey can carry a Forward/Reverse suffix (e.g. SHADOWFAX_REVERSE) purely to
     pick which field list to render, but the actual courier_code PCIS expects is just
     the plain parent name, same for both directions. Keeps tracking the parent name
     (e.g. if the user goes back and picks a different parent) as long as the field
     still holds our OWN suggestion — once the user types their own value (see `set`
     above) it's left alone. */
  const autoSuggested = useRef(true)
  useEffect(() => {
    if (DI.mode !== 'DIRECT' || !parentName) return
    if ((autoSuggested.current || !DI.partnerCode) && DI.partnerCode !== parentName) {
      autoSuggested.current = true
      dispatch({type: 'FIELD', slice: 'DI', key: 'partnerCode', value: parentName})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DI.mode, parentName])

  /* PCIS courier-partners lookup — a courier code can map to several entries
     (e.g. multiple DTDC rows, each with a different cp_id), so this fetches the
     list live as soon as a code is entered and the user picks the right one below.
     service_type is derived from the freight direction chosen earlier — both
     Reverse variants (with/without QC) send RVP, Forward sends FORWARD. */
  const serviceType = diServiceType(W.S.direction)
  const [lookupCourierPartners] = useLazyLookupCourierPartnersQuery()
  const [matches, setMatches] = useState([])
  const [lookupState, setLookupState] = useState('idle') // idle | loading | done | error
  const [lookupErrMsg, setLookupErrMsg] = useState('')
  const lookedUpFor = useRef('')

  useEffect(() => {
    if (DI.mode !== 'DIRECT' || isNewParent) return
    const code = String(DI.partnerCode || '').trim()
    if (!code) {
      setMatches([])
      setLookupState('idle')
      return
    }
    const cacheKey = code + '|' + serviceType
    const t = setTimeout(() => {
      if (lookedUpFor.current === cacheKey) return
      lookedUpFor.current = cacheKey
      setLookupState('loading')
      lookupCourierPartners({courierPartnerCode: code, serviceType})
        .unwrap()
        .then((result) => {
          setMatches(result || [])
          setLookupState('done')
          /* previously selected cp_id belonged to a different code/service type — clear it */
          const stillValid = (result || []).some((m) => String(m.cp_id) === String(DI.cpId))
          if (DI.cpId && !stillValid) dispatch({type: 'FIELD', slice: 'DI', key: 'cpId', value: ''})
        })
        .catch((e) => {
          setMatches([])
          setLookupState('error')
          setLookupErrMsg(apiError(e))
        })
    }, 400)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DI.mode, DI.partnerCode, serviceType])

  const pickMatch = (m) => {
    dispatch({type: 'FIELD', slice: 'DI', key: 'cpId', value: m.cp_id})
    clearInv('cpId')
  }

  const onNext = () => {
    const e = {}
    if (!DI.mode) e.mode = true
    if (DI.mode === 'CLICKPOST') {
      if (!String(DI.cpId || '').trim()) e.cpId = true
    } else if (DI.mode === 'DIRECT') {
      if (!String(DI.partnerCode || '').trim()) e.partnerCode = true
      if (!String(DI.cpId || '').trim()) e.cpId = true
      /* new-parent flow registers a brand-new PCIS courier partner — credentials
         are optional there (can be filled in later); existing-parent flow attaches
         to an already-registered partner and needs its schema fields up front. */
      if (!isNewParent) {
        const fields = DI.fields || {}
        schema.fields.forEach((f) => {
          if (f.type === 'toggle') return
          if (f.required && !String(fields[f.key] || '').trim()) e[f.key] = true
        })
      }
      /* an added custom field can't be left blank — both key and value are required
         (or the user should delete the row). Applies to both parent flows. */
      extras.forEach((x, i) => {
        if (!String(x.key || '').trim()) e[`x${i}k`] = true
        if (!String(x.value || '').trim()) e[`x${i}v`] = true
      })
    }
    setInv(e)
    scrollToFirstError()
    if (Object.keys(e).length) return
    goNext()
  }

  return (
    <div className='card card-pad' style={{maxWidth: 1400}}>
      <style>{CSS}</style>
      <h3 style={{margin: '0 0 4px'}}>Click Post / Direct Integration</h3>
      <p className='sub' style={{margin: '0 0 14px'}}>
        Choose how this courier account is integrated. Direct Integration connects straight to the courier's API (PCIS) instead of going through Click Post.
      </p>

      <div className={cx('mdsec', inv.mode && 'invalid')}>
        <div className='mdg2'>
          <div className={cx('mdcard', DI.mode === 'CLICKPOST' && 'sel')} onClick={() => setMode('CLICKPOST')}>
            <div>
              <div className='t'>Click Post</div>
              <div className='d'>Existing flow — routes through Click Post. Just needs the CP Id below.</div>
            </div>
            <span className='tick' />
          </div>
          <div className={cx('mdcard', DI.mode === 'DIRECT' && 'sel')} onClick={() => setMode('DIRECT')}>
            <div>
              <div className='t'>Direct Integration</div>
              <div className='d'>Connects directly to the courier's API. Requires additional courier credentials.</div>
            </div>
            <span className='tick' />
          </div>
        </div>
        {inv.mode ? <div className='vmsg'>Choose Click Post or Direct Integration.</div> : null}
      </div>

      {DI.mode === 'CLICKPOST' ? (
        <Field label='CP Id (Clickpost)' type='number' value={DI.cpId} onChange={(v) => set('cpId', v)} ph='e.g. 11' invalid={inv.cpId} help='Clickpost courier id for this account.' />
      ) : null}

      {DI.mode === 'DIRECT' ? (
        <>
          <div className='mdtarget'>
            <span className='ic'>
              <LocalShippingOutlinedIcon style={{fontSize: 17}} />
            </span>
            <div>
              <div className='lbl'>Direct Integration</div>
              <div className='val'>{DI.partnerCode || '—'}</div>
            </div>
          </div>

          {isNewParent ? (
            <Field
              label='Clickpost ID (cp_id)'
              type='number'
              value={DI.cpId}
              onChange={(v) => set('cpId', v)}
              ph='e.g. 12'
              invalid={inv.cpId}
              help="This courier partner doesn't exist in PCIS yet, so there's nothing to look up — enter its Clickpost id directly."
            />
          ) : DI.partnerCode ? (
            <div className={cx('mdsec', inv.cpId && 'invalid')}>
              <label style={{display: 'block', fontWeight: 700, fontSize: 12.5, marginBottom: 6}}>Courier partner (PCIS) *</label>
              {lookupState === 'loading' ? (
                <div style={{display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--neutral)'}}>
                  <CircularProgress size={14} /> Looking up "{DI.partnerCode}"…
                </div>
              ) : lookupState === 'error' ? (
                <div className='vmsg' style={{marginTop: 0}}>Lookup failed: {lookupErrMsg}</div>
              ) : matches.length ? (
                <div className='mdpp'>
                  {matches.map((m) => (
                    <div key={m.id} className={cx('mdcard', String(DI.cpId) === String(m.cp_id) && 'sel')} onClick={() => pickMatch(m)}>
                      <div>
                        <div className='t'>{m.courier_name}</div>
                        <div className='d'>
                          cp_id: {m.cp_id} · {m.integration_type} · {m.is_active ? 'Active' : 'Inactive'}
                        </div>
                      </div>
                      <span className='tick' />
                    </div>
                  ))}
                </div>
              ) : lookupState === 'done' ? (
                <div className='warnbox'>
                  <InfoOutlinedIcon />
                  <div>No courier partner entries found for "{DI.partnerCode}" — check the code or provision it in PCIS first.</div>
                </div>
              ) : null}
              {inv.cpId ? <div className='vmsg'>Select a courier partner entry above.</div> : null}
            </div>
          ) : null}

          <div className='mdg2'>
            {schema.fields.map((f) =>
              f.type === 'toggle' ? (
                <Field
                  key={f.key}
                  label={f.label}
                  type='select'
                  options={YN}
                  value={(DI.fields || {})[f.key] ? 'Yes' : 'No'}
                  onChange={(v) => setField(f.key, v === 'Yes')}
                  opt
                />
              ) : (
                <Field
                  key={f.key}
                  label={f.label}
                  value={(DI.fields || {})[f.key]}
                  onChange={(v) => setField(f.key, v)}
                  ph={f.ph}
                  invalid={inv[f.key]}
                  opt={isNewParent || !f.required}
                />
              )
            )}
          </div>

          <div className='xfhead'>
            <div>
              <div className='t'>Additional fields</div>
              <div className='s'>Optional. Add any extra credential as a key/value pair — sent alongside the fields above.</div>
            </div>
          </div>
          {extras.map((x, i) => (
            <div className='xfrow' key={i}>
              <input
                className={cx('input', 'in', inv[`x${i}k`] && 'err')}
                placeholder='Key (e.g. api_token)'
                value={x.key}
                onChange={(ev) => updateExtra(i, {key: ev.target.value})}
              />
              <input
                className={cx('input', 'in', inv[`x${i}v`] && 'err')}
                placeholder='Value'
                value={x.value}
                onChange={(ev) => updateExtra(i, {value: ev.target.value})}
              />
              <button type='button' className='xfdel' onClick={() => removeExtra(i)} title='Remove field'>
                <DeleteOutlineOutlinedIcon style={{fontSize: 18}} />
              </button>
            </div>
          ))}
          <button type='button' className='xfadd' onClick={addExtra}>
            <AddOutlinedIcon style={{fontSize: 17}} /> Add field
          </button>
        </>
      ) : null}

      <Footer step={W.step} onBack={goBack} onNext={onNext} />
    </div>
  )
}

StepMode.propTypes = {
  W: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
}
